// 상단 통합검색창 — 어느 화면에서든 업무 자료함의 파일·링크를 이름으로 찾아 바로 연다 (티켓 #87).
//
// 이 기능의 요구는 "모아두기"가 아니라 **"찾아서 바로 여는 것"**이었다. 그래서 자료함 메뉴로
// 들어가는 수고 없이, 헤더 아래 줄에 늘 떠 있는 이 한 칸에서 이름을 치면 결과가 즉시 뜨고
// 한 번의 클릭으로 열린다 — 파일은 곧바로 내려받고, 링크는 안전하다고 지정한 폴더만 새 탭으로
// 열며 나머지는 주소를 복사해 시크릿 창 안내를 띄운다(티켓 #162, #204).
//
// 검색 대상은 1차로 자료함 자료만이다(고객·신청서로 넓히는 건 별도 결정).
// 좁은 화면(모바일)에서도 이 칸은 유지한다 — 디데이·만나이 계산기만 xl 이상에서 보인다
// (2026-07-28 확정). 현장에서 휴대폰으로 자료를 여는 게 이 기능의 주 사용처라서다.
//
// 2026-08-12: 상단 띠의 아랫줄(검색 전용 한 줄)을 없애고 이 칸을 윗줄로 올렸다. 전 화면에
// 늘 떠 있는 띠라 한 줄을 통째로 쓰는 비용이 컸다. 대신 **평소엔 좁게, 누르면 넓게** —
// 넓어지는 동안 옆 컴포넌트(계산기·버튼들)는 헤더가 잠시 감춰 자리를 내준다. 그래서 이
// 컴포넌트는 자기가 지금 넓은 상태인지를 `onExpandedChange` 로 부모에게 알려준다.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import {
  formatBytes,
  fetchLibraryFolders,
  libraryQk,
  openLibraryItem,
  searchLibrary,
  type LibraryItem,
} from "../../api/library";
import LibraryKindIcon from "../library/LibraryKindIcon";
import { usePrivateLinkGuide } from "../library/PrivateLinkGuide";
import { usePrivacyMask } from "../../context/PrivacyMaskContext";
import { checkPrivacyPassword } from "../../api/privacyMask";
import { looksLikePrivacyPassword } from "../../../shared/privacy-lock";

type Props = {
  /** 바깥(헤더)이 폭을 정한다 — 좁을 때/넓을 때 클래스를 갈아 끼워 애니메이션을 준다. */
  className?: string;
  /** 넓어졌는지(=포커스했거나 글자가 남아 있는지)를 부모에게 알린다. */
  onExpandedChange?: (expanded: boolean) => void;
};

export default function GlobalSearchBar({ className = "", onExpandedChange }: Props) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [busyId, setBusyId] = useState<number | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // 글자마다 서버를 때리지 않게 250ms 만 모아 보낸다(현장에서 한 글자씩 지웠다 쓰는 일이 잦다).
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const { data, isFetching } = useQuery({
    queryKey: libraryQk.search(debounced),
    queryFn: () => searchLibrary(debounced),
    enabled: debounced.length > 0,
    staleTime: 30_000,
  });
  const { data: foldersData } = useQuery({
    queryKey: libraryQk.folders,
    queryFn: fetchLibraryFolders,
    staleTime: 30_000,
  });

  // ── 이 칸은 개인정보 가리기의 **암호 입력구를 겸한다**(티켓 #461) ──────────────
  // 잠금 팝업을 따로 만들지 않는 것이 이 요구의 핵심이다 — 점검 나온 사람 눈에는 그냥
  // 자료 검색칸이어야 한다. 그래서 여기 친 글자가 매장 암호와 맞으면 **친 글자를 즉시
  // 지우고**(안 지우면 옆에서 암호가 그대로 읽힌다) 헤더에 '가리기 해제' 버튼을 띄운다.
  // 자료 검색 기능 자체는 그대로 돈다.
  const { enabled: masking, armed, hasPassword, arm } = usePrivacyMask();
  useEffect(() => {
    // 평범한 검색어까지 매번 "암호 맞나"를 서버에 묻지 않는다 — 모양이 암호 같을 때만.
    if (!masking || armed || !hasPassword) return;
    if (!looksLikePrivacyPassword(debounced)) return;
    let cancelled = false;
    void checkPrivacyPassword(debounced).then((ok) => {
      if (cancelled || !ok) return;
      setQ("");
      setDebounced("");
      setOpen(false);
      inputRef.current?.blur();
      arm();
    });
    return () => {
      cancelled = true;
    };
  }, [debounced, masking, armed, hasPassword, arm]);

  const items = useMemo(() => data?.items ?? [], [data]);
  const directOpenFolderIds = useMemo(
    () =>
      new Set(
        (foldersData?.folders ?? [])
          .filter((folder) => folder.directOpen)
          .map((folder) => folder.id),
      ),
    [foldersData],
  );

  // 결과가 바뀌면 키보드 선택을 첫 줄로 되돌린다.
  useEffect(() => setActive(0), [items]);

  // 바깥을 누르면 결과창을 닫는다.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const { openPrivately, guide } = usePrivateLinkGuide();

  const openItem = async (item: LibraryItem) => {
    // 자료함 안의 모달 뷰어가 파일·글을 담당한다. 전역 검색에서는 그 자료가 보이도록 목록으로 보낸다.
    if (item.kind === "note" || item.kind === "file") {
      setOpen(false);
      navigate(`/library?q=${encodeURIComponent(item.title)}`);
      return;
    }
    setBusyId(item.id);
    try {
      // 안전하다고 지정한 폴더만 바로 열고, 나머지는 시크릿 창 안내로 연다 (#162, #204).
      const result = await openLibraryItem(item, { directOpenFolderIds });
      if (result.privateLink) void openPrivately(result.privateLink);
      setOpen(false);
    } finally {
      setBusyId(null);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      // 넓어진 칸을 접고 옆 컴포넌트를 되돌리려면 글자도 함께 비워야 한다(expanded 판정에 q 가 들어간다).
      setQ("");
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (!open || items.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % items.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i - 1 + items.length) % items.length);
    } else if (e.key === "Enter") {
      // 한글 입력 중(IME 조합)의 Enter 는 글자 확정이지 실행이 아니다.
      if ((e.nativeEvent as { isComposing?: boolean }).isComposing) return;
      e.preventDefault();
      void openItem(items[active]);
    }
  };

  const showPanel = open && debounced.length > 0;

  // 넓힌 채로 두는 조건 — 포커스 중이거나, 포커스를 잃었어도 친 글자가 남아 있을 때.
  // (글자를 쳐 놓고 결과를 보다가 다른 곳을 눌렀는데 칸이 확 줄어들면 읽던 맥락이 사라진다.)
  const expanded = open || q.trim().length > 0;
  useEffect(() => {
    onExpandedChange?.(expanded);
  }, [expanded, onExpandedChange]);

  return (
    <div ref={boxRef} className={`relative ${className}`}>
      <div className="relative">
        <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400">
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden>
            <path
              d="M9 15A6 6 0 109 3a6 6 0 000 12zM17 17l-3.5-3.5"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
        </span>
        <input
          ref={inputRef}
          type="search"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          // 접혀 있을 땐 칸이 좁아 긴 안내가 잘린다 — 두 글자로 줄이고 자세한 설명은 말풍선에
          // 맡긴다. ⚠️ 돋보기만 남기지는 않는다(2026-08-12 에 시도했다가 "뭔지 알아볼 수 없다"고
          // 되돌렸다). '자료 검색' 넉 자는 남기는 게 그때 배운 최소선이다.
          placeholder={
            expanded ? "자료 검색 — 파일·링크 이름을 치면 바로 열립니다" : "자료 검색"
          }
          aria-label="업무 자료함 통합검색"
          // 접혀 있을 땐 돋보기만 보여 글자로 설명할 자리가 없다 — 말풍선으로 대신한다.
          title="자료 검색 — 파일·링크 이름을 치면 바로 열립니다"
          className="h-9 w-full rounded-lg border border-gray-200 bg-gray-50 pl-9 pr-3 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-none focus:ring-2 focus:ring-brand-500/10 dark:border-gray-800 dark:bg-white/[0.03] dark:text-white/90 dark:placeholder:text-gray-500"
        />
      </div>

      {showPanel && (
        <div className="absolute left-0 right-0 top-full z-99999 mt-1 max-h-[60vh] min-w-[320px] overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-theme-lg dark:border-gray-800 dark:bg-gray-900">
          {items.length === 0 ? (
            <div className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">
              {isFetching ? "찾는 중…" : "일치하는 자료가 없습니다."}
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate("/library");
                }}
                className="ml-2 text-brand-500 hover:underline"
              >
                자료함 열기
              </button>
            </div>
          ) : (
            <>
              <ul className="py-1">
                {items.map((item, i) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onMouseEnter={() => setActive(i)}
                      onClick={() => void openItem(item)}
                      disabled={busyId === item.id}
                      className={`flex w-full items-center gap-3 px-3 py-2 text-left disabled:opacity-60 ${
                        i === active ? "bg-gray-50 dark:bg-white/[0.04]" : ""
                      }`}
                    >
                      <LibraryKindIcon kind={item.kind} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-gray-800 dark:text-white/90">
                          {item.title}
                        </span>
                        <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                          {item.kind === "link"
                            ? item.url
                            : item.kind === "note"
                              ? "화면에서 바로 읽는 글"
                              : [item.fileName, formatBytes(item.sizeBytes)]
                                .filter(Boolean)
                                .join(" · ")}
                          {item.kind !== "note" && item.description
                            ? ` — ${item.description}`
                            : ""}
                        </span>
                      </span>
                      <span className="shrink-0 text-[11px] text-gray-400">
                        {busyId === item.id
                          ? "여는 중…"
                            : item.kind === "link"
                              ? item.folderId != null && directOpenFolderIds.has(item.folderId)
                                ? "바로 열기"
                                : "주소 복사"
                              : "자료함에서 열기"}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate("/library");
                }}
                className="block w-full border-t border-gray-100 px-3 py-2 text-left text-xs text-brand-500 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-white/[0.04]"
              >
                업무 자료함에서 전체 보기 →
              </button>
            </>
          )}
        </div>
      )}

      {/* 링크를 눌렀을 때 뜨는 "시크릿 창에 붙여넣기" 안내 (티켓 #162) */}
      {guide}
    </div>
  );
}
